import React, {lazy} from "react";
import ReactDOM from "react-dom/client";
import reportWebVitals from "./reportWebVitals";
import {Provider} from "react-redux";
import {ThemeProvider} from "@mui/material";
import * as Sentry from "@sentry/react";
import {BrowserTracing} from "@sentry/tracing";
import {BrowserRouter, Route, Routes} from "react-router-dom";
import {retryFailLoad} from "modules/utils/LazyLoad";
import {
	ChangelogModal,
	ErrorModal,
	GameOverModal,
	Header,
	HOCi18n,
	HOCMain,
	HOCServiceSSO,
	Preloader,
	TutorialModal,
} from "components";

import {
	BASE_URL,
	PREPROD_URL,
	PROD_URL,
	PROD_URL_PUBLIC,
	SENTRY_DOMAIN_ALLOWED,
	UAT_URL,
	UAT_URL_PUBLIC,
} from "modules/constants";
import store from "modules/store";
import {theme} from "assets/theme";
import "assets/css/reset.css";
import "assets/css/core.css";
import "assets/fonts/fonts.css";
import {HOCDisabled} from "components/HOC/HOCDisabled";
import {ThanksForPlayingModal} from "components/Modals/ThanksForPlayingModal";
import {ContextualPromptBanners} from "components/ContextualPromptBanner";
// import UserProfile from "pages/UserProfile";

let errorCount = 0;
const MAX_ERRORS = 100;

const IGNORE_ERRORS = [
	"Authentication Required",
	"Load failed",
	"UET is not defined",
	"Loading chunk",
	"AbortError: Share canceled",
	"Failed to fetch",
	"Network Error",
	"Minified Redux error",
	"Unexpected end of script",
	"Abort due to cancellation of share",
	"The network connection was lost.",
	"Maximum call stack size exceeded",
	"Possible side-effect in debug-evaluate",
	"Native bridge was not set",
	"Initialization Error: Native bridge was not set",
	"UnhandledRejection: Non-Error promise rejection captured with keys: message, status",
	"Non-Error promise rejection captured with keys: message, status",
	"TypeError: undefined is not an object (evaluating 'ceCurrentVideo.currentTime')",
	"Identifier MlbJavascriptDataStore has already been declared",
	"Identifier 'MlbJavascriptDataStore' has already been declared",
	"SyntaxError: Identifier MlbJavascriptDataStore has already been declared",
	"SyntaxError: Identifier 'MlbJavascriptDataStore' has already been declared",
	"Java exception was raised during method invocation",
];

Sentry.init({
	dsn: "https://71be5b1d1ed84d928ee4be1bc6be32b9@o151969.ingest.sentry.io/6781491",
	environment: process.env.REACT_APP_SENTRY_ENV || "development",
	sampleRate: 0.1,
	tracesSampleRate: 0.1,
	allowUrls: [
		UAT_URL,
		UAT_URL_PUBLIC,
		PREPROD_URL,
		PROD_URL,
		PROD_URL_PUBLIC,
		SENTRY_DOMAIN_ALLOWED,
	],
	integrations: [
		new BrowserTracing(),
		new Sentry.Integrations.Breadcrumbs({
			console: false,
		}),
	],
	ignoreErrors: IGNORE_ERRORS,
	denyUrls: [
		"quantcast",
		"xsca",
		// browser's extensions
		/extensions\//i,
		/^chrome:\/\//i,
		/^moz-extension:\/\//i,
	],
	beforeSend: (event: Sentry.Event) => {
		errorCount++;
		if (errorCount > MAX_ERRORS) {
			return null;
		}
		if (event && event.message && IGNORE_ERRORS.includes(event.message)) {
			return null;
		}
		return event;
	},
});

const GamePage = lazy(retryFailLoad(() => import("pages/GamePage")));
const SummaryPage = lazy(retryFailLoad(() => import("pages/Summary")));
const Backdoor = lazy(retryFailLoad(() => import("pages/Backdoor")));
const Leaderboards = lazy(retryFailLoad(() => import("pages/Leaderboards")));
const UserProfile = lazy(retryFailLoad(() => import("pages/UserProfile")));

const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);

root.render(
	<React.StrictMode>
		<Provider store={store}>
			<ThemeProvider theme={theme}>
				<BrowserRouter basename={BASE_URL}>
					{/*<HOCDebugInformation>*/}
					<HOCi18n>
						<HOCDisabled>
							<HOCServiceSSO>
								<HOCMain>
									<React.Suspense fallback={<Preloader fixed />}>
										<Header />
										<Routes>
											<Route path="" element={<GamePage />} />
											<Route path="summary" element={<SummaryPage />} />
											<Route path="backdoor" element={<Backdoor />} />
											<Route
												path="leaderboards/:userId"
												element={<UserProfile />}
											/>
											<Route path="leaderboards" element={<Leaderboards />} />
											<Route path="profile" element={<UserProfile />} />
										</Routes>
										<ErrorModal />
										<TutorialModal />
										<GameOverModal />
										<ChangelogModal />
										<ThanksForPlayingModal />
										<ContextualPromptBanners />
									</React.Suspense>
								</HOCMain>
							</HOCServiceSSO>
						</HOCDisabled>
					</HOCi18n>
					{/*</HOCDebugInformation>*/}
				</BrowserRouter>
			</ThemeProvider>
		</Provider>
	</React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
